// Desktop bridge (SD7). Detects the Tauri 2 webview via the global API
// (`app.withGlobalTauri` in clients/desktop/tauri.conf.json) so this file ships
// in the shared web bundle with no `@tauri-apps/api` dependency — the same
// dist serves web and desktop.
//
// Native capture is not a second UI: the same recorder page starts/stops and
// uploads (SD25). When the shell reports native_capture_ready the screen path
// is ffmpeg (x11grab / avfoundation / gdigrab) instead of getDisplayMedia;
// MediaRecorder remains the fallback. Chunks still fsync via FR-RD7.

import type { HotkeyModifier } from "@grackle/ui";
import { configureApi, configureDesktopAuth, type DesktopAuthBridge } from "./api/client";

// PR4: event names shared by the main window and the floating HUD window (clients/desktop/src/
// hud.rs, tray.rs). Tray clicks and global hotkeys already emit record/start|stop|pause from
// Rust; the HUD's own buttons emit the same three from its webview so the main window's one
// listener (RecorderPage) drives capture no matter which of the three triggered it.
export const EVT_RECORD_START = "grackle://record/start";
export const EVT_RECORD_STOP = "grackle://record/stop";
export const EVT_RECORD_PAUSE = "grackle://record/pause";
/** F15a: the mark-moment global hotkey (lib.rs EVT_MARK). */
export const EVT_RECORD_MARK = "grackle://record/mark";
export const EVT_NAVIGATE_LIBRARY = "grackle://navigate/library";
/** Tray "Recent recordings" click (decision #5): payload is the recording id, main window
 * navigates to `/g/:id`. */
export const EVT_NAVIGATE_RECORDING = "grackle://navigate/recording";
/** FR-A4: the shell finished a sign-in (or sign-out) — `auth.rs`'s `EVT_AUTH_CHANGED`. */
export const EVT_AUTH_CHANGED = "grackle://auth/changed";
/** FR-A4: the deep-link callback arrived but no session came of it (`auth.rs`'s
 * `EVT_AUTH_FAILED`); payload is a short user-facing message. */
export const EVT_AUTH_FAILED = "grackle://auth/failed";
/** Main window -> HUD broadcast (decision #3), throttled to ~4-10 Hz by the emitter. */
export const EVT_HUD_STATE = "grackle://hud/state";
/** HUD -> main window (F8 / FR-RD15): "keep recording" during the window-closed countdown. */
export const EVT_MEETING_KEEP = "grackle://meeting/keep";

export interface HudUploadState {
  state: "uploading" | "done" | "offline";
  partsAccepted: number;
  partsCut: number;
}

export interface HudState {
  elapsedMs: number;
  paused: boolean;
  /** Mic input level, 0..1 */
  level: number;
  upload: HudUploadState;
  /** F8 / FR-RD15: set while recording a Zoom/Teams window. `closingIn` is the auto-stop
   * countdown in seconds once the meeting window has gone, else null. */
  meeting?: { app: "zoom" | "teams"; closingIn: number | null };
}

export interface TrayRecentItem {
  id: string;
  title: string;
}

export interface NativeFeature {
  available: boolean;
  ready: boolean;
  backend: string;
  reason: string | null;
}

export interface PermissionStatus {
  id: string;
  label: string;
  state: "granted" | "denied" | "prompt" | "unknown";
  how_to_grant: string;
  blocked_reason: string | null;
}

export interface DesktopCapabilities {
  os: string;
  modes: string[];
  audio: {
    microphone: boolean;
    system_loopback: boolean;
    system_loopback_backend: string;
    system_loopback_ready: boolean;
  };
  permissions: { id: string; label: string; how_to_grant: string }[];
  permission_states: PermissionStatus[];
  encode_preference: string;
  native_capture_ready: boolean;
  native_screen: NativeFeature;
  native_loopback: NativeFeature;
  session_kind: string;
  meeting_client_detection: boolean;
  /** F15a: native region capture works here (capture/region.rs + an ffmpeg grab that crops). */
  region_capture?: boolean;
}

export interface NativeCaptureSource {
  id: string;
  kind: "display" | "window" | "region";
  label: string;
  width: number;
  height: number;
  x: number;
  y: number;
}

export interface RecoverableSession {
  id: string;
  title: string;
  mime_type: string;
  bytes_on_disk: number;
  last_seq: number;
  state: "recording" | "stopped" | "finished";
  dir: string;
}

export interface NativeBridge {
  available: boolean;
  ready: boolean;
  backend: string;
  reason: string | null;
}

export interface MobileBridgeStatus {
  os: string;
  shell: string;
  camera: NativeBridge;
  microphone: NativeBridge;
  screen: NativeBridge;
  background_recording: NativeBridge;
  local_disk: NativeBridge;
  permissions: PermissionStatus[];
}

export interface DesktopInfo {
  desktop: boolean;
  mobile: boolean;
  /** `desktop` | `android` | `ios` (SD32). */
  shell: string;
  api_origin: string;
  data_dir: string;
  capabilities: DesktopCapabilities;
  recoverable: RecoverableSession[];
  hotkeys: HotkeysOut;
}

export interface SessionManifest {
  id: string;
  created_at_unix: number;
  state: "recording" | "stopped" | "finished";
  mime_type: string;
  title: string;
  next_seq: number;
  bytes_on_disk: number;
  last_seq: number;
}

interface TauriGlobal {
  core?: { invoke: (cmd: string, args?: Record<string, unknown>) => Promise<unknown> };
  event?: {
    listen: (event: string, handler: (event: { payload: unknown }) => void) => Promise<() => void>;
    /** `withGlobalTauri`'s app-wide emit — what lets the HUD window's buttons raise the same
     * events the tray and global hotkeys already do (decision #3). */
    emit?: (event: string, payload?: unknown) => Promise<void>;
  };
}

function tauri(): TauriGlobal | undefined {
  if (typeof window === "undefined") return undefined;
  const w = window as unknown as { __TAURI__?: TauriGlobal; __TAURI_INTERNALS__?: unknown };
  return w.__TAURI__;
}

/** True when the shared UI is running inside any Tauri 2 shell (desktop or mobile). */
export function isTauri(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as { __TAURI__?: unknown; __TAURI_INTERNALS__?: unknown };
  return Boolean(w.__TAURI__ || w.__TAURI_INTERNALS__);
}

/** True when the shared UI is running inside a Tauri 2 shell.
 *  Disk-first recording (FR-RD7) uses this — Android/iOS included (SD32). */
export function isDesktop(): boolean {
  return isTauri();
}

/** True after `loadDesktopInfo` when the shell is the Android/iOS hybrid app. */
export function isMobileShell(): boolean {
  if (!cachedInfo) return false;
  return cachedInfo.mobile || cachedInfo.shell === "android" || cachedInfo.shell === "ios";
}

export async function invoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const api = tauri()?.core;
  if (!api) throw new Error("Tauri invoke is not available");
  return api.invoke(cmd, args) as Promise<T>;
}

export async function listenDesktop(
  event: string,
  handler: () => void,
): Promise<() => void> {
  const api = tauri()?.event;
  if (!api) return () => {};
  return api.listen(event, () => handler());
}

export async function listenDesktopPayload<T>(
  event: string,
  handler: (payload: T) => void,
): Promise<() => void> {
  const api = tauri()?.event;
  if (!api) return () => {};
  return api.listen(event, (e) => handler(e.payload as T));
}

/** Raises a Tauri event from this webview for every other window's listeners — the HUD's
 * remote-control half of decision #3. A no-op outside Tauri (plain browser `/hud` preview). */
export async function emitDesktop(event: string, payload?: unknown): Promise<void> {
  const api = tauri()?.event;
  if (!api?.emit) return;
  await api.emit(event, payload);
}

let cachedInfo: DesktopInfo | null = null;

export async function loadDesktopInfo(opts?: { refresh?: boolean }): Promise<DesktopInfo | null> {
  if (!isDesktop()) return null;
  if (cachedInfo && !opts?.refresh) return cachedInfo;
  cachedInfo = await invoke<DesktopInfo>("desktop_info");
  if (cachedInfo.api_origin) {
    // Same hook the extension uses (FR-X5/X6): packaged builds may point at
    // staging/production via GRACKLE_API_ORIGIN. `tauri dev` leaves this empty
    // so the Vite proxy keeps same-origin `/api`.
    configureApi({ origin: cachedInfo.api_origin });
    // FR-A4: only once there's a real cross-origin API host does cookie auth stop working
    // (tauri://localhost / tauri.localhost never share a cookie jar with it) — wire the
    // bearer bridge so `client.ts`/`ws.ts` switch over. `tauri dev`'s empty origin keeps the
    // existing same-origin cookie path untouched. Same on the Android/iOS hybrid (SD33): its
    // webview is just as cross-origin, and the shell registers these commands there too.
    configureDesktopAuth(desktopAuthBridge(invoke));
  }
  return cachedInfo;
}

/** FR-A4: the bearer bridge over the shell's `auth_*` commands. Exported (with an injectable
 * `invoke`) for tests. A failed `auth_access_token` reads as "no token" rather than throwing:
 * the request then goes out unauthenticated and comes back as a real 401 the UI understands,
 * instead of every API call failing before it is sent — which the UI can only report as
 * "Couldn't reach Grackle". `auth_refresh` failures still propagate: the shell only rejects
 * that one when the API host itself was unreachable. */
export function desktopAuthBridge(
  call: <T>(cmd: string, args?: Record<string, unknown>) => Promise<T>,
): DesktopAuthBridge {
  return {
    getAccessToken: async () => {
      try {
        return await call<string | null>("auth_access_token");
      } catch (err) {
        console.warn("auth_access_token failed", err);
        return null;
      }
    },
    refresh: () => call<boolean>("auth_refresh"),
  };
}

export function peekDesktopInfo(): DesktopInfo | null {
  return cachedInfo;
}

export async function startDiskSession(mimeType: string, title: string): Promise<SessionManifest> {
  return invoke<SessionManifest>("recording_start", { mimeType, title });
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}

export async function appendDiskSegment(sessionId: string, chunk: Blob): Promise<SessionManifest> {
  const dataB64 = await blobToBase64(chunk);
  return invoke<SessionManifest>("recording_append", { sessionId, dataB64 });
}

export async function stopDiskSession(sessionId: string): Promise<SessionManifest> {
  return invoke<SessionManifest>("recording_stop", { sessionId });
}

export async function finishDiskSession(sessionId: string): Promise<SessionManifest> {
  return invoke<SessionManifest>("recording_finish", { sessionId });
}

export async function discardDiskSession(sessionId: string): Promise<void> {
  await invoke("recording_discard", { sessionId });
}

/** FR-RD14: the upload-progress sidecar next to manifest.json (`disk.rs`'s `upload.json`).
 * `null` means no sidecar has ever been written for this session — see
 * recovery/diskAdapter.ts for what that means for resume. */
export async function readUploadState(sessionId: string): Promise<string | null> {
  return invoke<string | null>("recording_read_upload_state", { sessionId });
}

export async function writeUploadState(sessionId: string, stateJson: string): Promise<void> {
  await invoke("recording_write_upload_state", { sessionId, stateJson });
}

export async function listRecoverableSessions(): Promise<RecoverableSession[]> {
  return invoke<RecoverableSession[]>("recording_list_recoverable");
}

export async function readDiskSegment(sessionId: string, seq: number): Promise<Blob> {
  const b64 = await invoke<string>("recording_read_segment", { sessionId, seq });
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes]);
}

export async function setNativeRecordingFlag(active: boolean): Promise<void> {
  if (!isDesktop()) return;
  await invoke("set_recording_flag", { active });
}

export async function listNativeSources(): Promise<NativeCaptureSource[]> {
  return invoke<NativeCaptureSource[]>("list_capture_sources");
}

export async function nativePreviewJpeg(sourceId: string): Promise<string> {
  return invoke<string>("native_preview_jpeg", { sourceId });
}

export async function nativeCaptureStart(opts: {
  sourceId: string;
  includeSystemAudio: boolean;
  includeMic: boolean;
}): Promise<string> {
  return invoke<string>("native_capture_start", opts);
}

export async function nativeCaptureStop(): Promise<void> {
  await invoke("native_capture_stop");
}

export async function nativeCapturePause(paused: boolean): Promise<void> {
  await invoke("native_capture_pause", { paused });
}

export async function nativeAudioPush(dataB64: string): Promise<void> {
  await invoke("native_audio_push", { dataB64 });
}

export function base64ToBlob(b64: string, mimeType: string): Blob {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

export async function loadMobileBridge(): Promise<MobileBridgeStatus | null> {
  if (!isTauri()) return null;
  return invoke<MobileBridgeStatus>("mobile_bridge_status");
}

export async function requestMobilePermissions(kinds: string[]): Promise<MobileBridgeStatus> {
  return invoke<MobileBridgeStatus>("mobile_request_permissions", { kinds });
}

export async function mobileCaptureStart(opts: {
  mode: "camera" | "screen" | "microphone";
  includeMic: boolean;
}): Promise<MobileBridgeStatus> {
  return invoke<MobileBridgeStatus>("mobile_capture_start", opts);
}

export async function mobileCaptureStop(): Promise<MobileBridgeStatus> {
  return invoke<MobileBridgeStatus>("mobile_capture_stop");
}

export function float32ToS16leBase64(samples: Float32Array): string {
  const bytes = new Uint8Array(samples.length * 2);
  for (let i = 0; i < samples.length; i++) {
    const v = Math.max(-1, Math.min(1, samples[i]));
    const s = v < 0 ? Math.round(v * 0x8000) : Math.round(v * 0x7fff);
    bytes[i * 2] = s & 0xff;
    bytes[i * 2 + 1] = (s >> 8) & 0xff;
  }
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

export interface RecorderHotkeys {
  startStop: (HotkeyModifier | string)[];
  pause: (HotkeyModifier | string)[];
  mark: (HotkeyModifier | string)[];
}

/** F15a: the configurable bindings as Tauri accelerator strings (hotkeys.rs `HotkeysOut`).
 * `mark` is optional for shells that predate F15a. */
export interface HotkeysOut {
  start_stop: string;
  pause: string;
  mark?: string;
}

export type HotkeyActionId = "start_stop" | "pause" | "mark";

/** FR-RD5 defaults, used in a plain browser and before `desktop_info` has loaded. Must match
 * the accelerators `clients/desktop/src/lib.rs::run` registers. */
const DEFAULT_HOTKEYS: RecorderHotkeys = {
  startStop: ["Mod", "Shift", "G"],
  pause: ["Mod", "Shift", "P"],
  mark: ["Mod", "Shift", "M"],
};

/** Turns a Tauri accelerator string ("CommandOrControl+Shift+G") into `formatHotkey`'s key
 * list. `null` for an empty string (mobile's `HotkeyInfo`, which has no global hotkeys). */
function parseAccelerator(accelerator: string): (HotkeyModifier | string)[] | null {
  if (!accelerator) return null;
  return accelerator
    .split("+")
    .map((part) => (part === "CommandOrControl" || part === "Command" || part === "Control" ? "Mod" : part));
}

/** The recorder's global hotkeys (decision #1): read from the desktop bridge when present,
 * falling back to the FR-RD5 defaults on web or before `desktop_info` resolves. Takes the info
 * explicitly (defaulting to the cached one) so this stays a pure function for tests. */
export function recorderHotkeys(info: DesktopInfo | null = peekDesktopInfo()): RecorderHotkeys {
  return {
    startStop: (info && parseAccelerator(info.hotkeys.start_stop)) ?? DEFAULT_HOTKEYS.startStop,
    pause: (info && parseAccelerator(info.hotkeys.pause)) ?? DEFAULT_HOTKEYS.pause,
    mark: (info && parseAccelerator(info.hotkeys.mark ?? "")) ?? DEFAULT_HOTKEYS.mark,
  };
}

/** `formatHotkey`'s key list for one accelerator string (empty when unset). */
export function acceleratorKeys(accelerator: string | undefined): (HotkeyModifier | string)[] {
  return parseAccelerator(accelerator ?? "") ?? [];
}

const CODE_KEYS: Record<string, string> = {
  Space: "Space",
  Enter: "Enter",
  Tab: "Tab",
  Backspace: "Backspace",
  Delete: "Delete",
  Insert: "Insert",
  Home: "Home",
  End: "End",
  PageUp: "PageUp",
  PageDown: "PageDown",
  ArrowUp: "Up",
  ArrowDown: "Down",
  ArrowLeft: "Left",
  ArrowRight: "Right",
};

/** A pressed key combination as the accelerator string hotkeys.rs parses, or `null` while only
 * modifiers are held. Uses `KeyboardEvent.code`, so the result doesn't depend on the layout's
 * shifted character. Ctrl (or ⌘) becomes `CommandOrControl`, which the shell maps per OS. */
export function acceleratorFromKeyEvent(e: {
  code: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}): string | null {
  let key: string | null = null;
  const m = /^(?:Key([A-Z])|Digit([0-9])|(F[0-9]{1,2}))$/.exec(e.code);
  if (m) key = m[1] ?? m[2] ?? m[3];
  else if (CODE_KEYS[e.code]) key = CODE_KEYS[e.code];
  if (!key) return null;
  const parts: string[] = [];
  if (e.ctrlKey || e.metaKey) parts.push("CommandOrControl");
  if (e.altKey) parts.push("Alt");
  if (e.shiftKey) parts.push("Shift");
  parts.push(key);
  return parts.join("+");
}

/** F15a: the bindings the desktop shell currently has registered. */
export async function getHotkeys(): Promise<HotkeysOut> {
  return invoke<HotkeysOut>("hotkeys_get");
}

/** Rebind one action. Rejects with the shell's message, e.g. "That shortcut is taken by
 * another app"; the old binding stays live then. */
export async function setHotkey(action: HotkeyActionId, accelerator: string): Promise<HotkeysOut> {
  const next = await invoke<HotkeysOut>("hotkeys_set", { action, accelerator });
  if (cachedInfo) cachedInfo = { ...cachedInfo, hotkeys: next };
  return next;
}

export async function resetHotkeys(): Promise<HotkeysOut> {
  const next = await invoke<HotkeysOut>("hotkeys_reset");
  if (cachedInfo) cachedInfo = { ...cachedInfo, hotkeys: next };
  return next;
}

/** Shows the floating HUD window bottom-center on the primary monitor and hides the main
 * window (decision #4). Called once capture actually starts, not at the start of the 3-2-1
 * countdown — the countdown itself is still worth watching in the main window. */
export async function hudShow(): Promise<void> {
  if (!isDesktop()) return;
  await invoke("hud_show");
}

/** Closes the HUD and shows + focuses the main window, which then renders PR3's
 * share-after-stop state (decision #4). */
export async function hudHide(): Promise<void> {
  if (!isDesktop()) return;
  await invoke("hud_hide");
}

/** Pushes "Recent recordings" to the native tray menu (decision #5). Rust never sees a session
 * token — this is the existing library list, trimmed to `{id, title}`. */
export async function setTrayRecent(signedIn: boolean, items: TrayRecentItem[]): Promise<void> {
  if (!isDesktop()) return;
  await invoke("tray_set_recent", { signedIn, items });
}

/** Decode a recoverable session back into Blobs the upload queue can push (FR-RD7). */
export async function replayRecoverable(session: RecoverableSession): Promise<Blob[]> {
  const chunks: Blob[] = [];
  for (let seq = 1; seq <= session.last_seq; seq++) {
    chunks.push(await readDiskSegment(session.id, seq));
  }
  return chunks;
}

// ---- F7: OS notifications (tauri-plugin-notification) ---------------------------------
// The plugin's commands are called through the global `invoke` rather than the
// `@tauri-apps/plugin-notification` package, for the same reason this file avoids
// `@tauri-apps/api`: one dist serves web and desktop, with no extra npm dependency.
// Desktop notifications carry no click event in Tauri 2, so the caller reopens the link on
// the next window focus (see notifications/useNotifications.ts).

export type OsNotificationPermission = "granted" | "denied" | "default";

export async function notificationPermissionGranted(): Promise<boolean> {
  return Boolean(await invoke<boolean | null>("plugin:notification|is_permission_granted"));
}

export async function requestNotificationPermission(): Promise<OsNotificationPermission> {
  return invoke<OsNotificationPermission>("plugin:notification|request_permission");
}

export async function sendOsNotification(title: string, body?: string): Promise<void> {
  await invoke("plugin:notification|notify", { options: { title, body } });
}

/** F14b (FR-RD10): a newer signed desktop build offered by the release `latest.json`. */
export interface UpdateInfo {
  version: string;
  current_version: string;
  notes: string | null;
}

/** `null` when up to date. Rejects when the channel can't be reached (callers stay silent). */
export async function checkForUpdate(): Promise<UpdateInfo | null> {
  return invoke<UpdateInfo | null>("updater_check");
}

/** Downloads, verifies, installs and restarts. Refused while a recording is running. */
export async function installUpdate(): Promise<void> {
  await invoke<void>("updater_install");
}
